import { Box, Stack, Typography } from "@mui/material";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { DEFAULT_VARIANT_TITLE } from "@smartrr/shared/constants";
import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { AquariusQuantity } from "@smartrr/shared/customerPortal/components/AquariusQuantity";
import { extractShopifyId, viewShopifyId } from "@smartrr/shared/utils/ensureShopifyGid";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { useVariantShopifyIDToPurchasableMap } from "@smartrr/shared/utils/useVariantToPurchasableMap";
import truncate from 'lodash/truncate';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { useCleanedAndSortedPurchables } from "@customer-app/state/reducers/purchasables";

import { OrderItemImage } from "../../../../../constants";
import { IBundleItemProps } from "../types";

export const BundleBuilderItem = ({
  item,
  updateCart,
  useAddButton,
  cartRef,
  maxProductQuantity,
  cartQuantityRef,
}: IBundleItemProps) => {
  const { quantity: cartQuantity, id: variantShopifyId } = item;

  const [quantity, setQuantity] = useState(cartQuantity);

  const [showAddButton, setShowAddButton] = useState<boolean>(!cartQuantity);

  const variantShopifyIdToPurchasableMap = useVariantShopifyIDToPurchasableMap(
    useCleanedAndSortedPurchables().purchasables
  );

  const purchasable = useMemo(() => variantShopifyIdToPurchasableMap[variantShopifyId], [variantShopifyId]);

  const purchasableVariant = useMemo(
    () => (purchasable?.vnts || []).find(vnt => viewShopifyId(vnt.shopifyId) === String(variantShopifyId)),
    [purchasable?.vnts]
  );

  const onAdd = useCallback(() => {
    if (maxProductQuantity === cartQuantityRef.current) {
      toast.error(typedLocalize("editBundle.maxItemsErrorToast"));
    } else {
      updateCart(variantShopifyId, 1);
      setShowAddButton(false);
    }
  }, [variantShopifyId]);

  useEffect(() => {
    if (cartQuantity) {
      setQuantity(cartQuantity);
      setShowAddButton(false);
    }
  }, [cartRef.current, cartQuantity, showAddButton]);

  useEffect(() => {
    if (!showAddButton && !cartRef?.current?.some(item => String(item.id) === String(variantShopifyId))) {
      setShowAddButton(true);
      setQuantity(0);
    }
  }, [cartRef.current]);

  if (!purchasable) {
    return <React.Fragment />;
  }

  const shopifyId = purchasableVariant?.shopifyId
    ? extractShopifyId(purchasableVariant.shopifyId)
    : extractShopifyId(purchasable.shopifyId);

  return (
    <Stack
      data-smartrr-shopify-variant-id={String(shopifyId)}
      spacing="7px"
      maxWidth="90px"
      sx={{
        "[class*='quantity-container']": {
          maxHeight: "30px",
        },
      }}
    >
      <OrderItemImage
        className={prefixClassName("item-product-image")}
        purchasable={purchasable}
        purchasableVariant={purchasableVariant}
        size="medium"
      />

      {useAddButton && showAddButton ? (
        <AquariusButton onClick={() => onAdd()}>{typedLocalize("general.add")}</AquariusButton>
      ) : (
        <AquariusQuantity
          onChange={quantity => {
            updateCart(variantShopifyId, quantity);
            if (useAddButton && quantity === 0) {
              setShowAddButton(true);
            }
          }}
          value={quantity}
        />
      )}

      <Box display="flex" flexDirection="column">
        <Typography variant="body1" className={prefixClassName("product-name")}>
          {truncate(purchasable?.purchasableName)}
        </Typography>
        {purchasableVariant?.purchasableVariantName === DEFAULT_VARIANT_TITLE ? null : (
          <Typography
            variant="caption1"
            className={prefixClassName("product-variant-name")}
            sx={{ width: "100%" }}
          >
            {truncate(purchasableVariant?.purchasableVariantName)}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};
